// S29 批次匯入(/aid/batches)— 以 ca-manager 為主。上傳 PKI 產出的 NDJSON
// (did/cert_pem/enc_private_key)入池、看匯入批次 manifest。同步匯入、單型別。
// 後端 = restful 代理 /v3/admin/imports → ca-manager(未落地 → 誠實缺口)。
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../../lib/api';
import { ErrorState, LoadingState } from '../../components/StateBlock';
import { useAuth } from '../../auth/AuthContext';
import { canAidAllocWrite } from './aidAllocationModel';
import { importCerts, listImports, type ImportBatch } from './aidSupplyApi';
import '../_shared/shared.css';
import './aid.css';

function fmtDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toISOString().slice(0, 19).replace('T', ' ');
}
function isPendingBackend(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 404 || err.status === 501);
}

export default function AidBatchesPage() {
  const { role } = useAuth();
  const writable = canAidAllocWrite(role);

  const [items, setItems] = useState<ImportBatch[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<unknown>(null);

  const [source, setSource] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const page = await listImports();
      setItems(page.items);
    } catch (e) {
      setErr(e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = useCallback(async () => {
    if (!file) {
      setMsg({ ok: false, text: '請選擇 NDJSON 檔。' });
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const b = await importCerts(file, source);
      setMsg({ ok: true, text: `匯入 ${b.type}:入池 ${b.inserted}、略過 ${b.skipped}、失敗 ${b.failed}(共 ${b.submitted})。` });
      setFile(null);
      setSource('');
      if (fileRef.current) fileRef.current.value = '';
      void load();
    } catch (e) {
      setMsg({ ok: false, text: e instanceof ApiError ? `${e.code}: ${e.message}` : String(e) });
    } finally {
      setBusy(false);
    }
  }, [file, source, load]);

  return (
    <div className="aid-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">批次匯入</h1>
          <p className="page-subtitle">
            上傳 PKI 產出的憑證 NDJSON 入池(同步、單一 AID 類型)。後端 = ca-manager(<span className="adm-mono">/v3/admin/imports</span>)。不簽發。
          </p>
        </div>
      </div>

      {msg && (
        <p className={`adm-action-result ${msg.ok ? 'is-ok' : 'is-err'}`} role={msg.ok ? 'status' : 'alert'}>
          {msg.text}
        </p>
      )}

      <div className="aid-split">
        <section className="card">
          <div className="section-head">
            <div>
              <h2 className="section-title">匯入批次(清單檔)</h2>
              <span className="section-meta">每筆 = 一次匯入;入池/略過/失敗計數</span>
            </div>
            {loading && <span className="pill pill-neutral">查詢中</span>}
          </div>
          {err && isPendingBackend(err) ? (
            <p className="aid-gap-note">
              <strong>誠實缺口:</strong>後端 <span className="adm-mono">/v3/admin/imports</span>(→ ca-manager)尚未落地。
            </p>
          ) : err ? (
            <ErrorState error={err} onRetry={() => void load()} />
          ) : loading && items.length === 0 ? (
            <LoadingState />
          ) : items.length > 0 ? (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>批次</th>
                    <th>AID 類型</th>
                    <th>來源</th>
                    <th>入池/略過/失敗</th>
                    <th>時間</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((b) => (
                    <tr key={b.import_id}>
                      <td className="adm-mono">{b.import_id}</td>
                      <td className="adm-mono">{b.type}</td>
                      <td>{b.source ?? '—'}</td>
                      <td className="adm-mono">{b.inserted}/{b.skipped}/{b.failed}</td>
                      <td className="adm-mono">{fmtDate(b.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">📥</div>
              <div className="empty-state-title">尚無匯入批次</div>
              <div className="empty-state-detail">{writable ? '右側上傳 NDJSON 開始。' : '由 admin 匯入。'}</div>
            </div>
          )}
        </section>

        <aside className="aid-side">
          <section className={`card ${writable ? 'card--primary' : ''}`}>
            <div className="section-head">
              <h2 className="section-title">上傳匯入</h2>
            </div>
            {writable ? (
              <div className="aid-form">
                <label className="adm-field">
                  <span className="adm-field-label text-secondary">NDJSON 檔(必填)</span>
                  <input ref={fileRef} type="file" accept=".ndjson,.jsonl,.txt,application/x-ndjson" onChange={(e) => setFile(e.target.files?.[0] ?? null)} disabled={busy} />
                </label>
                <label className="adm-field">
                  <span className="adm-field-label text-secondary">來源標記(選填)</span>
                  <input value={source} onChange={(e) => setSource(e.target.value)} disabled={busy} placeholder="簽發批次 / 檔名" />
                </label>
                <p className="aid-hint">同步匯入:一批須為單一 AID 類型(did 前綴一致);已存在的 DID 會被略過(冪等)。</p>
                <button className="btn-primary btn-sm" onClick={() => void submit()} disabled={busy || !file}>
                  匯入入池
                </button>
              </div>
            ) : (
              <p className="aid-hint">僅 admin / platform-owner 可匯入。</p>
            )}
          </section>
        </aside>
      </div>
    </div>
  );
}
